import React, { useState, useEffect } from 'react';
import { Star, User } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../utils/api';

// Star Rating Display Component
export const StarRating = ({ rating, size = 'md', showCount = false, count = 0 }) => {
  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} ${
            star <= rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-stone-200 text-stone-200'
          }`}
        />
      ))}
      {showCount && (
        <span className="text-sm text-stone-500 ml-1">
          ({count} {count === 1 ? 'review' : 'reviews'})
        </span>
      )}
    </div>
  );
};

// Interactive Star Rating Input
export const StarRatingInput = ({ rating, setRating }) => {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          <Star
            className={`h-8 w-8 ${
              star <= (hovered || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-stone-200 text-stone-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Seller Rating Summary Card
export const SellerRatingSummary = ({ sellerId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/reviews/seller/${sellerId}/summary`);
        setSummary(res.data);
      } catch (err) {
        console.error('Failed to fetch rating summary', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [sellerId]);

  if (loading || !summary) return null;
  if (summary.review_count === 0) return null;

  return (
    <div className="flex items-center gap-2">
      <StarRating rating={summary.average_rating} size="sm" />
      <span className="text-sm font-medium text-stone-700">
        {summary.average_rating}
      </span>
      <span className="text-sm text-stone-500">
        ({summary.review_count} {summary.review_count === 1 ? 'review' : 'reviews'})
      </span>
    </div>
  );
};

// Review Form Component
export const ReviewForm = ({ sellerId, sellerName, listingId, onSuccess }) => {
  const { user, api } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await api().post('/reviews', {
        seller_id: sellerId,
        listing_id: listingId,
        rating,
        comment: comment.trim() || null
      });
      toast.success('Review submitted successfully!');
      setRating(0);
      setComment('');
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Failed to submit review', err);
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-stone-200 rounded-sm p-6">
      <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
        Rate your experience with {sellerName}
      </h3>

      <div className="mb-4">
        <p className="text-sm text-stone-500 mb-2">How would you rate this seller?</p>
        <StarRatingInput rating={rating} setRating={setRating} />
      </div>

      <div className="mb-4">
        <p className="text-sm text-stone-500 mb-2">Share your experience (optional)</p>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Tell others about your experience..."
          rows={3}
          className="resize-none"
        />
      </div>

      <Button
        type="submit"
        disabled={submitting || rating === 0}
        className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
      >
        {submitting ? (
          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
        ) : (
          'Submit Review'
        )}
      </Button>
    </form>
  );
};

// Reviews List Component
export const ReviewsList = ({ sellerId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/reviews/seller/${sellerId}`);
        setReviews(res.data);
      } catch (err) {
        console.error('Failed to fetch reviews', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [sellerId]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-stone-100 h-24 rounded-sm" />
        ))}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <p className="text-stone-500 text-center py-8">
        No reviews yet. Be the first to leave a review!
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => (
        <div key={review.id} className="bg-white border border-stone-200 rounded-sm p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                <User className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="font-medium text-stone-900">{review.buyer_name}</p>
                <StarRating rating={review.rating} size="sm" />
              </div>
            </div>
            <p className="text-xs text-stone-400">
              {new Date(review.created_at).toLocaleDateString('en-AU')}
            </p>
          </div>
          
          {review.comment && (
            <p className="mt-3 text-stone-600 text-sm">{review.comment}</p>
          )}
          
          {review.listing_title && (
            <p className="mt-2 text-xs text-stone-400">
              Purchased: {review.listing_title}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default { StarRating, StarRatingInput, SellerRatingSummary, ReviewForm, ReviewsList };
